import React from 'react';
import { Checkbox } from '@naf-simba/elements-checkbox';
import { UseFormReturn } from 'react-hook-form';
import { TextVariant } from '@naf/text';
import { TextListItem } from '@naf/text-list';
import { Label, Message } from '@naf/input';
import type { MembersFormType } from '@appTypes/membersFormType';
import { BirthDateDay } from '@components/Form/fieldInputs/BirthDateDay';
import { BirthDateMonth } from '@components/Form/fieldInputs/BirthDateMonth';
import { BirthDateYear } from '@components/Form/fieldInputs/BirthDateYear';
import { formattedPrice } from '@lib/formattedPrice';
import { PhoneFieldController } from '@shared/components/PhoneInputWithCountryCode';
import { FirstName } from '../Members/fieldControllers/FirstName';
import { LastName } from '../Members/fieldControllers/LastName';
import { EMail } from '../Members/fieldControllers/EMail';
import AddonFormStyles from './Form/Styles';
import S from './Styles';
import { householdMemberIndex } from './Addons.utils';

type HouseholdMember = NonNullable<MembersFormType['otherMembers']>[number];

type HouseholdMemberErrors = {
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  eMail?: string;
  birthDate?: {
    day?: string;
    month?: string;
    year?: string;
  };
};

interface HouseholdMemberAddonProps {
  control: UseFormReturn['control'];
  errors?: HouseholdMemberErrors;
  householdMemberPrice: number;
  isSelected: boolean;
  member?: Partial<HouseholdMember>;
  onChange: () => void;
}

export const HouseholdMemberAddon = ({
  control,
  errors,
  householdMemberPrice,
  isSelected,
  member,
  onChange,
}: HouseholdMemberAddonProps) => {
  const birthDateErrors = [errors?.birthDate?.day, errors?.birthDate?.month, errors?.birthDate?.year].filter(Boolean);

  return (
    <AddonFormStyles.FieldWrapper $error={!!errors}>
      <AddonFormStyles.CheckboxWrapper>
        <S.HouseholdTitle variant={TextVariant.Header3}>Ekstra medlem i husstanden</S.HouseholdTitle>
        <S.StyledTextList variant="check">
          <TextListItem>Passer spesielt godt for par eller familiemedlemmer på samme adresse</TextListItem>
          <TextListItem>Begge får personlig tilgang til alle medlemsfordeler og rabatter</TextListItem>
          {householdMemberPrice > 0 && (
            <TextListItem>Kun {formattedPrice(householdMemberPrice)} kr per år</TextListItem>
          )}
        </S.StyledTextList>
        <Checkbox label="Legg til ekstra medlem" name="household-member" checked={isSelected} onChange={onChange} />
      </AddonFormStyles.CheckboxWrapper>
      {isSelected && (
        <AddonFormStyles.FormFieldsWrapper>
          <div>
            <S.HouseholdTitle variant={TextVariant.Header3}>Medlem 2</S.HouseholdTitle>
            <S.HouseholdSubtitle>Vennligst fyll ut feltene nedenfor.</S.HouseholdSubtitle>
            <S.FieldWrapper>
              <PhoneFieldController
                name={`otherMembers.${householdMemberIndex}.phoneNumber`}
                control={control}
                defaultValue={member?.phoneNumber}
                errorMessage={errors?.phoneNumber}
              />
            </S.FieldWrapper>
            <S.ControllersWrapper>
              <FirstName
                name={`otherMembers.${householdMemberIndex}.firstName`}
                control={control}
                defaultValue={member?.firstName}
                rules={{ required: 'Vennligst fyll inn dette feltet.' }}
                errorMessage={errors?.firstName}
              />
              <LastName
                name={`otherMembers.${householdMemberIndex}.lastName`}
                control={control}
                defaultValue={member?.lastName}
                rules={{ required: 'Vennligst fyll inn dette feltet.' }}
                errorMessage={errors?.lastName}
              />
            </S.ControllersWrapper>
            <S.FieldWrapper>
              <EMail
                name={`otherMembers.${householdMemberIndex}.eMail`}
                control={control}
                defaultValue={member?.eMail}
                errorMessage={errors?.eMail}
              />
            </S.FieldWrapper>
            <S.FieldWrapper>
              <S.BirthDateWrapper as="div">
                <Label>Fødselsdato</Label>
                <BirthDateDay
                  name={`otherMembers.${householdMemberIndex}.birthDate.day`}
                  member={`otherMembers.${householdMemberIndex}`}
                  control={control}
                  defaultValue={member?.birthDate?.day}
                  rules={{ required: 'Dato' }}
                  errorMessage={errors?.birthDate?.day}
                />
                <BirthDateMonth
                  name={`otherMembers.${householdMemberIndex}.birthDate.month`}
                  member={`otherMembers.${householdMemberIndex}`}
                  control={control}
                  defaultValue={member?.birthDate?.month}
                  rules={{ required: 'Måned' }}
                  errorMessage={errors?.birthDate?.month}
                />
                <BirthDateYear
                  name={`otherMembers.${householdMemberIndex}.birthDate.year`}
                  member={`otherMembers.${householdMemberIndex}`}
                  control={control}
                  defaultValue={member?.birthDate?.year}
                  rules={{ required: 'År' }}
                  errorMessage={errors?.birthDate?.year}
                />
              </S.BirthDateWrapper>
              {birthDateErrors.length > 0 && <Message error>{birthDateErrors.join(', ')}</Message>}
            </S.FieldWrapper>
          </div>
        </AddonFormStyles.FormFieldsWrapper>
      )}
    </AddonFormStyles.FieldWrapper>
  );
};
